import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import PageHero from "../components/PageHeader";
import { VESSELS, PAGE_HEROES } from "../data/site";

function VesselCard({ vessel, index }) {
  return (
    <article
      data-testid={`fleet-card-${vessel.slug}`}
      className="surface-card rounded-md overflow-hidden flex flex-col rise-in"
      style={{ animationDelay: `${0.06 * index}s` }}
    >
      <Link to={`/fleet/${vessel.slug}`} className="block" data-testid={`fleet-card-${vessel.slug}-link`}>
        <div className="img-frame aspect-[4/3]">
          <img src={vessel.image} alt={vessel.name} loading="lazy" />
        </div>
      </Link>
      <div className="p-6 md:p-7 flex-1 flex flex-col">
        <Link to={`/fleet/${vessel.slug}`} className="hover:text-[#1a3a5c] transition-colors">
          <h3 className="font-serif text-2xl md:text-[1.6rem] text-[#13263c] leading-tight">
            {vessel.shortName}
          </h3>
        </Link>
        <p className="mt-2 text-[#4a4a4a] leading-snug min-h-[2.6rem]">
          {vessel.line}
        </p>

        {/* Spec table */}
        <div className="mt-5 border border-[#e3ddcf] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {vessel.specs.map((s, i) => (
                <tr
                  key={s.label}
                  className={i % 2 === 0 ? "bg-[#fbf8f1]" : "bg-[#f3ecdc]"}
                >
                  <td className="py-2.5 px-3 text-[#4a4a4a]">{s.label}</td>
                  <td className="py-2.5 px-3 text-right text-[#2b2b2b] font-medium">{s.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <hr className="accent-rule mt-6" />
        <p className="mt-4 text-[#13263c]">
          <span className="font-serif text-2xl md:text-3xl">{vessel.rateValue}</span>
          {vessel.rateSuffix && (
            <span className="font-sans text-sm ml-1 text-[#4a4a4a]"> {vessel.rateSuffix}</span>
          )}
          <span className="font-sans text-sm text-[#4a4a4a]"> / 24 </span>
          <span className="font-sans text-sm text-[#4a4a4a]">hours</span>
        </p>
        {vessel.trailerOption && (
          <p className="mt-2 text-xs text-[#737373]" data-testid={`fleet-card-${vessel.slug}-trailer`}>
            {vessel.trailerOption}
          </p>
        )}
      </div>
    </article>
  );
}

export default function Fleet() {
  const hero = PAGE_HEROES.fleet;
  return (
    <div data-testid="fleet-page" className="bg-[#f7f4ec]">
      <PageHero image={hero.image} title={hero.title} subtitle={hero.subtitle} testId="fleet-hero" />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24">
        {/* Section header with side rules */}
        <div className="flex items-center justify-center gap-6 md:gap-10">
          <hr className="flex-1 hairline" />
          <div className="text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-[#13263c] tracking-tight">Our Fleet</h2>
            <p className="mt-2 text-[#4a4a4a]">Quality, reliable vessels for your day on the water.</p>
          </div>
          <hr className="flex-1 hairline" />
        </div>

        {/* 4-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7 mt-12 md:mt-16">
          {VESSELS.map((v, i) => (
            <VesselCard key={v.slug} vessel={v} index={i} />
          ))}
        </div>

        {/* 24-hour rental window callout */}
        <div
          data-testid="fleet-rental-callout"
          className="mt-12 md:mt-16 surface-card-soft rounded-md px-8 py-6 md:px-12 md:py-8 flex items-center justify-center gap-6 md:gap-8"
        >
          <div className="h-14 w-14 rounded-full border-2 border-[#13263c] flex items-center justify-center text-[#13263c] flex-shrink-0">
            <Clock size={26} />
          </div>
          <div>
            <h3 className="font-serif text-xl md:text-2xl text-[#13263c] leading-tight">
              24-hour rental window.
            </h3>
            <p className="text-[#4a4a4a] mt-1">More time on the water.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
